import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { Form } from '@anytag/shared-atoms';
import { usePostPricesAvailability } from '../../hooks';
import { AnyCreatorIframePreview } from '../AnyCreatorIframePreview';
import {
  TitleField,
  RegionField,
  BudgetField,
  CountryField,
  RevenueField,
  SalePICField,
  HashtagField,
  AdvertiserField,
  MarginRateField,
  HubspotDealField,
  CostPerPostField,
  CostPerLikeField,
  SocialMediaField,
  CostPerViewField,
  CostPerShareField,
  ApprovePostsField,
  PreLaunchDateField,
  AgeRangeInputField,
  FormLayoutTemplate,
  MaterialUploadField,
  CostPerCommentField,
  PostRequirementField,
  ThumbnailUploadField,
  GenderCheckBoxesField,
  AgencyMarginRateField,
  CampaignCategoryField,
  AllowNewInfluencerField,
  ApproveInfluencersField,
  ServiceInformationField,
  InfluencerCategoryField,
  FollowerRangeInputField,
  DraftPostSubmissionField,
  PostTrackingEndDateField,
  CampaignMaterialLinkField,
  AdditionalRequirementField,
  InfluencerManagementPICField,
  CampaignPeriodWithLogicsField
} from '../shared';
import { DefaultFormTemplateProps, MarketplaceFormValues } from '../types';
import { marketplaceSectionTitles } from '../utils';
import { AiRainbowSpinner, DescriptionGenerator, useDescriptionGeneratorState } from '../../DescriptionGenerator';

export const Template = ({ defaultValues, disabledFields, onSubmit }: DefaultFormTemplateProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext<MarketplaceFormValues>();
  const { isAdminStaff, isAdvertiser, isAdminStaffAgency } = useUserRoles();
  const descriptionActions = useDescriptionGeneratorState();

  const { socialMedia } = watch();
  const defaultStep = isAdvertiser ? -1 : 0;
  const allPostPrices = usePostPricesAvailability(socialMedia);

  return (
    <FormLayoutTemplate defaultValues={defaultValues} onSubmit={onSubmit}>
      {!isAdvertiser ? (
        <>
          <Form.SectionTitle index={defaultStep + 1} className="mb-6" label={t(marketplaceSectionTitles.picSettings)} />
          <Form.Fields itemsPerRow={2}>
            {isAdminStaffAgency ? <AdvertiserField disabled={disabledFields.advertiserId} required /> : null}
            {isAdminStaff ? (
              <>
                <HubspotDealField disabled={disabledFields.hubspotDealIds} />
                <SalePICField disabled={disabledFields.salesPicIds} required />
                <InfluencerManagementPICField disabled={disabledFields.influencerManagementPicIds} required />
              </>
            ) : null}
          </Form.Fields>
          <Form.Line className="mx-auto my-6" />
        </>
      ) : null}

      <Form.SectionTitle
        index={defaultStep + 2}
        className="mb-4"
        label={t(marketplaceSectionTitles.campaignInformation)}
      />
      <p className="mb-3 text-sm">{t('socialMediaTrackingInformation')}</p>

      <Form.Fields itemsPerRow={4}>
        <PreLaunchDateField disabled={disabledFields.preLaunchDate} />
        <CampaignPeriodWithLogicsField disabled={disabledFields.period} required />
        <PostTrackingEndDateField disabled={disabledFields.postTrackingEndDate} />
      </Form.Fields>

      <Form.Fields itemsPerRow={3}>
        <CountryField disabled={disabledFields.countryId} required />
        <RegionField disabled={disabledFields.regionIds} />
        <CampaignCategoryField disabled={disabledFields.campaignCategoryId} required />
        <SocialMediaField disabled={disabledFields.socialMedia} required />
        <InfluencerCategoryField disabled={disabledFields.influencerCategoryIds} required />
      </Form.Fields>

      <Form.Fields itemsPerRow={3}>
        <GenderCheckBoxesField disabled={disabledFields.genders} required />
      </Form.Fields>

      <Form.Fields itemsPerRow={3}>
        <AgeRangeInputField
          minField={{ disabled: disabledFields.minAge }}
          maxField={{ disabled: disabledFields.maxAge }}
        />
        <FollowerRangeInputField
          minField={{ disabled: disabledFields.minFollowers }}
          maxField={{ disabled: disabledFields.maxFollowers }}
          socialMedia={socialMedia}
        />
      </Form.Fields>

      <Form.Field>
        <div className="border-base-border px-4">
          <ApproveInfluencersField disabled={disabledFields.isAutoInfluencerApproval} />
          <ApprovePostsField disabled={disabledFields.isAllowMultiplePosts} />
          <AllowNewInfluencerField disabled={disabledFields.allowNewInfluencer} />
        </div>
      </Form.Field>

      <Form.Line className="mx-auto my-6" />

      <DescriptionGenerator {...descriptionActions}>
        <Form.SectionTitle
          index={defaultStep + 3}
          className="mb-1"
          label={t(marketplaceSectionTitles.campaignDetails)}
        />
      </DescriptionGenerator>

      <TitleField
        required
        disabled={disabledFields.title || descriptionActions.loading}
        labelLoader={descriptionActions.loading ? <AiRainbowSpinner className="ml-1" /> : undefined}
      />
      <ServiceInformationField
        disabled={disabledFields.serviceInformation || descriptionActions.loading}
        required
        getSunEditorInstance={descriptionActions.getSunEditorInstance}
        labelLoader={descriptionActions.loading ? <AiRainbowSpinner className="ml-1" /> : undefined}
      />
      <PostRequirementField
        disabled={disabledFields.requirement || descriptionActions.loading}
        required
        getSunEditorInstance={descriptionActions.getSunEditorInstance}
        labelLoader={descriptionActions.loading ? <AiRainbowSpinner className="ml-1" /> : undefined}
      />
      <DraftPostSubmissionField disabled={disabledFields.requireDraftPost} />
      <ThumbnailUploadField disabled={disabledFields.thumbnails} required />
      <MaterialUploadField disabled={disabledFields.materials} />
      <AdditionalRequirementField disabled={disabledFields.additionalRequirement} />
      <HashtagField disabled={disabledFields.hashtags} required />
      <CampaignMaterialLinkField disabled={disabledFields.campaignMaterialLink} />

      <Form.Line className="mx-auto mt-3 mb-6" />
      <Form.SectionTitle
        index={defaultStep + 4}
        className="mb-1"
        label={t(marketplaceSectionTitles.paymentInformation)}
      />

      <Form.Fields itemsPerRow={4}>
        {allPostPrices.costPerPost ? <CostPerPostField disabled={disabledFields.costPerPost} /> : null}
        {allPostPrices.costPerLike ? <CostPerLikeField disabled={disabledFields.costPerLike} /> : null}
        {allPostPrices.costPerView ? <CostPerViewField disabled={disabledFields.costPerView} /> : null}
        {allPostPrices.costPerShare ? <CostPerShareField disabled={disabledFields.costPerShare} /> : null}
        {allPostPrices.costPerComment ? <CostPerCommentField disabled={disabledFields.costPerComment} /> : null}
      </Form.Fields>

      <Form.Line className="mx-auto my-6" />
      <Form.SectionTitle index={defaultStep + 5} className="mb-1" label={t(marketplaceSectionTitles.budgetSettings)} />

      <Form.Fields itemsPerRow={4}>
        <RevenueField disabled={disabledFields.maximumRevenuePerInfluencer} />
        {isAdminStaffAgency ? <AgencyMarginRateField disabled={disabledFields.agencyMarginRate} /> : null}
      </Form.Fields>

      <Form.Fields itemsPerRow={4}>
        {isAdminStaff ? <MarginRateField disabled={disabledFields.marginRate} required /> : null}
        <BudgetField disabled={disabledFields.budget} required />
      </Form.Fields>

      <AnyCreatorIframePreview />
    </FormLayoutTemplate>
  );
};
